<script setup lang="ts">
import { useRouter } from 'vue-router'
import { ref } from 'vue'
import { loadLoginMethod, useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { Constants } from '@/utils/enums'

// props
// ログアウト直後・セッション切れ直後の遷移で true になる
const props = defineProps<{ authError: boolean }>()

// init
const loginJson = ref({
  user_account_id: '',
  password: '',
})
const adminLoginJson = ref({
  email: '',
  password: '',
})
const authStore = useAuthStore()
const userStore = useUserStore()
const router = useRouter()
// VITE_ENTRA_ID_SSO_ENABLED=true の環境でのみ通常ユーザに Entra SSO ログインを表示する
const isSsoEnabled = Constants.ENTRA_ID_SSO_ENABLED === 'true'
const isLoading = ref(true)
const isAdminMode = ref(false)

function isAdminSession() {
  const method = loadLoginMethod()
  if (method !== null) return method === 'local'
  return userStore.get_current_user.localCredential !== null
}

// セッションが有効な場合はログインをスキップする
if (props.authError) {
  isLoading.value = false
} else if (authStore.is_loggedin) {
  isAdminMode.value = isAdminSession()
  next()
} else {
  isLoading.value = false
  authStore
    .authedHello()
    .then(() => {
      isAdminMode.value = isAdminSession()
      next()
    })
    .catch(() => {
      isLoading.value = false
    })
}

// login
function login() {
  if (isAdminMode.value) {
    // Admin login
    const json = { ...adminLoginJson.value }
    authStore
      .localLogin(json)
      .then(() => {
        next()
      })
      .catch((err: unknown) => {
        console.log(err)
      })
  } else {
    // Normal login
    const json = { ...loginJson.value }
    authStore
      .login(json)
      .then(() => {
        next()
      })
      .catch((err: unknown) => {
        console.log(err)
      })
  }
}
function next() {
  if (isAdminMode.value) {
    router.push('/admin/salesrep-dashboard')
  } else {
    router.push('/chat')
  }
}
</script>

<template>
  <div v-if="isLoading" v-loading="isLoading" class="loading"></div>
  <div v-else class="container">
    <div id="login">
      <div class="logo">Wismettac Sales AI</div>
      <div class="switch-wrapper">
        <div class="switch-container">
          <span class="switch-label" :class="{ active: !isAdminMode }">User Login</span>
          <el-switch v-model="isAdminMode" />
          <span class="switch-label" :class="{ active: isAdminMode }">Admin Login</span>
        </div>
      </div>
      <div class="form-wrapper">
        <el-form v-if="!isAdminMode && !isSsoEnabled" label-position="top">
          <el-form-item label="Account ID">
            <el-input v-model="loginJson.user_account_id" placeholder="example.everforth" />
          </el-form-item>
          <el-form-item label="Password">
            <el-input
              v-model="loginJson.password"
              type="Password"
              placeholder="(WeConnect Password)"
              @keyup.enter="login()"
            />
          </el-form-item>
        </el-form>
        <el-form v-else-if="isAdminMode" label-position="top">
          <el-form-item label="Email">
            <el-input v-model="adminLoginJson.email" placeholder="admin@example.com" />
          </el-form-item>
          <el-form-item label="Password">
            <el-input
              v-model="adminLoginJson.password"
              type="Password"
              placeholder="Admin Password"
              @keyup.enter="login()"
            />
          </el-form-item>
        </el-form>
      </div>
      <div class="actions">
        <el-button
          v-if="!isAdminMode && isSsoEnabled"
          class="submit white"
          @click="authStore.entraLogin()"
        >
          Login with SSO
        </el-button>
        <el-button v-else class="submit white" @click="login()">
          {{ isAdminMode ? 'Admin Login' : 'Login' }}
        </el-button>
      </div>
    </div>
  </div>
</template>
<style scoped>
.container {
  height: 100dvh;
  width: 100vw;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}
#login {
  width: 100%;
  max-width: 360px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  box-sizing: border-box;
  padding: 40px 24px 32px;
  backdrop-filter: blur(10px);
  animation: fadeInUp 0.4s ease-out;
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.logo {
  text-align: center;
  color: #2c3e50;
  font-size: 28px;
  font-weight: bold;
  line-height: 36px;
  margin-bottom: 8px;
}
.switch-wrapper {
  padding: 20px 0;
  display: flex;
  justify-content: center;

  .switch-container {
    display: flex;
    align-items: center;
    gap: 12px;

    .switch-label {
      font-size: 14px;
      color: #909399;
      transition: color 0.3s;

      &.active {
        color: #2c3e50;
        font-weight: 600;
      }
    }
  }
}
.form-wrapper {
  box-sizing: border-box;
  padding-top: 24px;
  padding-bottom: 32px;
}
.el-form-item {
  margin-bottom: 0;
}
.el-form-item + .el-form-item {
  margin-top: 20px;
}
#login :deep(.el-input__wrapper) {
  background: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) inset;
}
#login :deep(.el-input__inner) {
  height: 48px;
  line-height: 48px;
  font-size: 16px;
  background: transparent;
}
#login :deep(.el-form-item__label) {
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}
#login :deep(.el-form--label-top .el-form-item__label) {
  padding: 0;
}
#login :deep(.el-input__wrapper:hover) {
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.05) inset,
    0 0 0 1px #e0e0e0;
}
#login :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 2px #409eff;
}
#login :deep(.el-input__inner) {
  font-family: inherit;
  color: #2c3e50;
}
#login :deep(.el-input__inner::placeholder) {
  color: #a0a0a0;
}
.actions {
  text-align: center;
  margin-top: 32px;
}
.actions .el-button {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  background: #409eff;
  border: none;
  color: white;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
  transition: all 0.3s ease;
}
.actions .el-button:hover {
  background: #66b1ff;
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.4);
  transform: translateY(-2px);
}
.actions .el-button:active {
  transform: translateY(0);
}
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100dvh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}
/* モバイル対応 */
@media (max-width: 480px) {
  #login {
    max-width: 100%;
    margin: 0 10px;
    padding: 32px 20px 24px;
  }
  .logo {
    font-size: 24px;
  }
  #login :deep(.el-input__inner) {
    font-size: 16px;
  }
}
</style>
